<template>
  <div id="user-edit-tab-info">
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="vx-col w-full mb-base">
          <vs-progress
            v-if="isLoadingRecords"
            indeterminate
            color="primary"
            class="mb-4"
          />
          <div v-if="loadError" class="hours-rendered-tab-error mb-4">
            <span>Loan records could not be loaded.</span>
            <vs-button type="border" size="small" @click="loadLoansData">
              Try again
            </vs-button>
          </div>
          <div
            v-if="!isLoadingRecords && !loadError"
            class="hours-rendered-record-switch mb-4"
          >
            <button
              type="button"
              :class="{ active: recordView === 'active' }"
              @click="recordView = 'active'"
            >
              <feather-icon icon="CheckCircleIcon" svgClasses="h-4 w-4" />
              Current Active
              <span>{{ activeLoans.length }}</span>
            </button>
            <button
              type="button"
              :class="{ active: recordView === 'history' }"
              @click="recordView = 'history'"
            >
              <feather-icon icon="ClockIcon" svgClasses="h-4 w-4" />
              History
              <span>{{ loanHistory.length }}</span>
            </button>
          </div>
          <template>
            <vs-table
              v-if="!isLoadingRecords && sortedLoans.length"
              stripe
              max-items="4"
              pagination
              search
              :data="sortedLoans"
            >
              <template slot="thead">
                <vs-th @click.native="sort('loansTypeId')">Loan Type</vs-th>
                <vs-th @click.native="sort('referenceNo')">Reference No</vs-th>
                <vs-th @click.native="sort('loanDate')">Loan Date</vs-th>
                <vs-th @click.native="sort('principalAmount')"
                  >Principal Amount</vs-th
                >
                <vs-th @click.native="sort('amortizationAmount')">Amortization</vs-th>
                <vs-th @click.native="sort('balance')">Balance</vs-th>
                <vs-th @click.native="sort('periodId')">Period</vs-th>
                <vs-th @click.native="sort('isHold')">Hold?</vs-th>
                <vs-th @click.native="sort('isActive')">Active?</vs-th>
                <vs-th>Action</vs-th>
              </template>
              <template slot-scope="{ data }">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="data[indextr].loansTypeId">
                    {{ loanTypeLabel(data[indextr].loansTypeId) }}
                  </vs-td>
                  <vs-td :data="data[indextr].referenceNumber">
                    {{ data[indextr].referenceNumber }}
                  </vs-td>
                  <vs-td :data="data[indextr].loanDate">
                    {{ formatLoanDate(data[indextr].loanDate) }}
                  </vs-td>

                  <vs-td :data="data[indextr].principalAmount">
                    {{ data[indextr].principalAmount }}
                  </vs-td>
                  <vs-td :data="data[indextr].amortizationAmount">
                    {{ data[indextr].amortizationAmount }}
                  </vs-td>
                  <vs-td :data="data[indextr].balance">
                    {{ data[indextr].balance }}
                  </vs-td>
                  <vs-td :data="data[indextr].periodId">
                    {{ periodLabel(data[indextr].periodId) }}
                  </vs-td>
                  <vs-td :data="data[indextr].isHold">
                    <vs-checkbox
                      v-model="data[indextr].isHold"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td :data="data[indextr].isActive">
                    <vs-checkbox
                      v-model="data[indextr].isActive"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td>
                    <div :style="{ direction: $vs.rtl ? 'rtl' : 'ltr' }">
                      <vx-tooltip text="Edit"
                        ><feather-icon
                          icon="Edit3Icon"
                          svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer"
                          @click="editRecord(data[indextr].id)"
                      /></vx-tooltip>
                      <!-- TODO: Add delete after record-deletion rules and permissions are finalized. -->
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </template>
          <div
            v-if="!isLoadingRecords && !loadError && !sortedLoans.length"
            class="hours-rendered-record-empty"
          >
            <feather-icon
              :icon="recordView === 'active' ? 'CheckCircleIcon' : 'ClockIcon'"
              svgClasses="h-8 w-8"
            />
            <h4>{{ recordView === "active" ? "No active loans" : "No loan history" }}</h4>
            <p>
              {{
                recordView === "active"
                  ? "Add a loan for this employee to get started."
                  : "Inactive loans will appear here."
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="vx-row">
      <div class="vx-col w-full">
        <vs-button
          v-if="recordView === 'active'"
          type="gradient"
          color="primary"
          :disabled="isLoadingRecords || loadError"
          @click="promptAddNewLoans()"
          icon-pack="feather"
          icon="icon-plus"
          >Add</vs-button
        >
      </div>
    </div>

    <!-- ADD LOANS -->
    <vs-prompt
      class="email-compose"
      title="Add Loans"
      accept-text="Submit"
      @accept="onAddRecord"
      :is-valid="validForm"
      :active.sync="activePromptAddRecord"
    >
      <vx-card title="Please fill-out the following information">
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Loan Type</span>
            <span style="color: red">*</span>
            <v-select
              name="loan type"
              v-validate="'required'"
              class="w-full"
              v-model="loanType"
              label="loanTypeName"
              :options="getAllLoanType"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('loan type')"
              >{{ errors.first("loan type") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Reference Number</span>
            <vs-input
              class="w-full"
              placeholder="Reference Number"
              v-model="referenceNumber"
            />
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Loan Date</span
            ><span style="color: red">*</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="loanDate"
            />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Principal Amount</span
            ><span style="color: red">*</span>
            <vs-input
              name="principal amount"
              v-validate="'max_value:10000000'"
              class="w-full"
              v-model="principalAmount"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('principal amount')"
              >{{ errors.first("principal amount") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Period</span><span style="color: red">*</span>
            <v-select
              name="period"
              v-validate="'required'"
              class="w-full"
              v-model="period"
              label="periodName"
              :options="getAllPeriodByPaymentMode"
              placeholder="Please Select"
            />
            <span class="text-danger text-sm" v-show="errors.has('period')">{{
              errors.first("period")
            }}</span>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Amortization Amount</span
            ><span style="color: red">*</span>
            <vs-input
              name="amortization amount"
              v-validate="'min_value:.01'"
              class="w-full"
              v-model="amortizationAmount"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('amortization amount')"
              >{{ errors.first("amortization amount") }}</span
            >
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Balance</span>
            <vs-input
              name="balance"
              class="w-full"
              v-model="balance"
            />
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/3 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isHold">Hold?</vs-checkbox>
            </div>
          </div>
          <div class="vx-col sm:w-1/3 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isActive">Active?</vs-checkbox>
            </div>
          </div>
        </div>
      </vx-card>
    </vs-prompt>
    <!-- END ADD EARNINGS -->
    <!-- EDIT EARNINGS -->
    <vs-prompt
      class="email-compose"
      title="Edit Loans"
      accept-text="Submit"
      @accept="onUpdateRecord"
      :is-valid="validForm"
      :active.sync="activePromptEditRecord"
    >
      <vx-card title="You may update the following information">
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Loan Type</span>
            <span style="color: red">*</span>
            <v-select
              name="loan type"
              v-validate="'required'"
              class="w-full"
              v-model="loanType"
              label="loanTypeName"
              :options="getAllLoanType"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('loan type')"
              >{{ errors.first("loan type") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Reference Number</span>
            <vs-input
              class="w-full"
              placeholder="Reference Number"
              v-model="referenceNumber"
            />
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Loan Date</span
            ><span style="color: red">*</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="loanDate"
            />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Principal Amount</span
            ><span style="color: red">*</span>
            <vs-input
              name="principal amount"
              v-validate="'max_value:10000000'"
              class="w-full"
              v-model="principalAmount"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('principal amount')"
              >{{ errors.first("principal amount") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Period</span><span style="color: red">*</span>
            <v-select
              name="period"
              v-validate="'required'"
              class="w-full"
              v-model="period"
              label="periodName"
              :options="getAllPeriodByPaymentMode"
              placeholder="Please Select"
            />
            <span class="text-danger text-sm" v-show="errors.has('period')">{{
              errors.first("period")
            }}</span>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Amortization Amount</span
            ><span style="color: red">*</span>
            <vs-input
              name="amortization amount"
              v-validate="'min_value:.01'"
              class="w-full"
              v-model="amortizationAmount"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('amortization amount')"
              >{{ errors.first("amortization amount") }}</span
            >
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Balance</span>
            <vs-input
              name="balance"
              class="w-full"
              v-model="balance"
            />
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/3 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isHold">Hold?</vs-checkbox>
            </div>
          </div>
          <div class="vx-col sm:w-1/3 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isActive">Active?</vs-checkbox>
            </div>
          </div>
        </div>
      </vx-card>
    </vs-prompt>
    <!-- END EDIT EARNINGS -->
  </div>
</template>
<script>
import vSelect from "vue-select";

import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

const normalizeLoanDate = (value = new Date()) => {
  if (typeof value === "string") {
    const datePart = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (datePart) return datePart[0];
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default {
  props: {
    paymentMode: {
      type: Number,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selected: [],

      loansId: 0,
      loanType: null,
      referenceNumber: "",
      loanDate: normalizeLoanDate(),
      principalAmount: 0,
      amortizationAmount: 0,
      balance: 0,
      period: null,
      isHold: false,
      isActive: true,
      activePromptAddRecord: false,
      activePromptEditRecord: false,
      isLoadingRecords: false,
      loadError: false,
      recordView: "active",

      configdateTimePicker: {
        allowInput: true,
        dateFormat: "Y-m-d",
      },
      sortKey: "",
      sortOrder: 1, // 1 for ascending, -1 for descending
    };
  },
  components: {
    "v-select": vSelect,
    flatPickr,
  },
  computed: {
    getAllLoans() {
      return this.$store.getters["payroll/getLoansByUserId"](this.userId);
    },
    getAllLoanType() {
      return this.$store.state.lookup.getAllLoanType;
    },
    getAllPeriodByPaymentMode() {
      return this.$store.getters["lookup/periodLookupByPaymentMode"](
        this.paymentMode
      );
    },
    loanTypeName() {
      return (id) => this.$store.getters["lookup/loanTypeLookup"](id);
    },
    periodName() {
      return (id) => this.$store.getters["lookup/periodLookup"](id);
    },
    validForm() {
      return Boolean(
        this.loanType &&
          this.loanType.loanTypeId &&
          this.loanDate &&
          this.principalAmount !== "" &&
          this.principalAmount !== null &&
          Number(this.principalAmount) > 0 &&
          this.amortizationAmount !== "" &&
          this.amortizationAmount !== null &&
          this.period &&
          this.period.periodId
      );
    },
    activeLoans() {
      return this.getAllLoans.filter((loan) => Boolean(loan.isActive));
    },
    loanHistory() {
      return this.getAllLoans.filter((loan) => !loan.isActive);
    },
    displayedLoans() {
      return this.recordView === "history" ? this.loanHistory : this.activeLoans;
    },
    sortedLoans() {
      if (this.sortKey) {
        const sortedData = this.displayedLoans.slice().sort((a, b) => {
          let aValue = a[this.sortKey];
          let bValue = b[this.sortKey];

          if (aValue == null) return this.sortOrder;
          if (bValue == null) return -this.sortOrder;

          if (typeof aValue === "number" && typeof bValue === "number") {
            return this.sortOrder * (aValue - bValue);
          }
          return this.sortOrder * aValue.localeCompare(bValue);
        });

        return sortedData;
      } else {
        return this.displayedLoans;
      }
    },
  },
  methods: {
    loadLoansData() {
      return this.$store.dispatch("payroll/preloadHoursRenderedData", {
        force: true,
      });
    },
    loanTypeLabel(id) {
      const loanType = this.loanTypeName(id);
      return loanType ? loanType.loanTypeName : "Unknown loan type";
    },
    periodLabel(id) {
      const period = this.periodName(id);
      return period ? period.periodName : "Loading…";
    },
    formatLoanDate(value) {
      return normalizeLoanDate(value);
    },
    promptAddNewLoans() {
      this.resetLoansForm();
      this.activePromptAddRecord = true;
    },
    resetLoansForm() {
      this.loansId = 0;
      this.loanType = null;
      this.referenceNumber = "";
      this.loanDate = normalizeLoanDate();
      this.principalAmount = 0;
      this.amortizationAmount = 0;
      this.balance = 0;
      this.period = null;
      this.isHold = false;
      this.isActive = true;
      this.$nextTick(() => this.$validator.reset());
    },
    editRecord(id) {
      const e = this.$store.getters["payroll/getLoansById"](id);
      if (!e) return;

      this.loanType = this.loanTypeName(e.loansTypeId);
      this.referenceNumber = e.referenceNumber;
      this.loanDate = normalizeLoanDate(e.loanDate);
      this.principalAmount = e.principalAmount;
      this.amortizationAmount = e.amortizationAmount;
      this.balance = e.balance;
      this.period = this.periodName(e.periodId);
      this.isHold = e.isHold;
      this.isActive = e.isActive;
      this.loansId = e.id;
      this.activePromptEditRecord = true;
    },
    onAddRecord() {
      this.$vs.loading();
      const e = {
        loansTypeId: this.loanType.loanTypeId,
        referenceNumber: this.referenceNumber,
        loanDate: normalizeLoanDate(this.loanDate),
        principalAmount: this.principalAmount,
        balance: this.balance,
        amortizationAmount: this.amortizationAmount,
        periodId: this.period.periodId,
        isHold: this.isHold,
        isActive: this.isActive,
        userId: this.userId,
      };
      this.$store
        .dispatch("payroll/AddEmployeeLoans", e)
        .then(() => {
          this.$vs.loading.close();
        })
        .catch((error) => {
          this.handleSaveError(error);
        });
    },
    onUpdateRecord() {
      this.$vs.loading();
      const e = {
        loansTypeId: this.loanType.loanTypeId,
        referenceNumber: this.referenceNumber,
        loanDate: normalizeLoanDate(this.loanDate),
        principalAmount: this.principalAmount,
        balance: this.balance,
        amortizationAmount: this.amortizationAmount,
        periodId: this.period.periodId,
        isHold: this.isHold,
        isActive: this.isActive,
        userId: this.userId,
        id: this.loansId,
      };
      this.$store
        .dispatch("payroll/UpdateEmployeeLoans", e)
        .then(() => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Success",
            text: "Loan updated",
            iconPack: "feather",
            icon: "icon-check",
            color: "success",
          });
        })
        .catch((error) => {
          this.handleSaveError(error);
        });
    },
    handleSaveError(error) {
      this.$vs.loading.close();
      this.$vs.notify({
        title: "Unable to save",
        text: error && error.message ? error.message : "Please try again.",
        iconPack: "feather",
        icon: "icon-x-octagon",
        color: "danger",
      });
    },
    sort(key) {
      if (this.sortKey === key) {
        this.sortOrder *= -1;
      } else {
        this.sortKey = key;
        this.sortOrder = 1;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
@import "@/assets/scss/vuexy/apps/simple-calendar.scss";
</style>
